// ---------------------------------------------------------------------------
// Cel-shading (#1). Everything here is procedural — the toon ramp is generated
// in code as a tiny texture, no image files. MeshToonMaterial samples a 1-D
// "gradient map" by the surface's N·L term; NearestFilter turns the smooth
// diffuse falloff into a few hard bands → the cartoon look.
//
// Plan 23 adds `toonify()` — a rim/fresnel light injected into EVERY toon
// material via onBeforeCompile (shader-string augmentation, not a material
// swap, so fog/shadows/vertexColors/instancing all keep working). SERVER-SAFE:
// this module is on the headless world-build path (server/maps.ts → buildWorld
// → meshkit/terrain/structure → here), and shader strings / compile hooks /
// DataTexture writes are all inert without a renderer. NO DOM in this file.
// ---------------------------------------------------------------------------
import * as THREE from "three";

/** Bake a 1×N greyscale ramp as a DataTexture. Each stop is one toon band.
 *  `filter` defaults to NearestFilter (hard cel steps); pass LinearFilter for a
 *  smoothly-interpolated ramp (the curved-prim ramp below). */
function ramp(stops: number[], filter: THREE.MagnificationTextureFilter = THREE.NearestFilter): THREE.DataTexture {
  const data = new Uint8Array(stops.length * 4);
  for (let i = 0; i < stops.length; i++) {
    const v = Math.round(stops[i]! * 255);
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, stops.length, 1, THREE.RGBAFormat);
  tex.magFilter = filter;
  tex.minFilter = filter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

// The toon shader maps dotNL from [-1,1] onto the ramp's [0,1] — so the LOWER
// HALF of the ramp is surfaces facing AWAY from a light. Those texels must be
// BLACK: a non-zero floor there means every light (fireplace, lamp, sun)
// illuminates straight through geometry onto back-facing walls. The upper half
// keeps the three bands (deep shadow → mid → lit) with edges at dotNL 0, ⅓, ⅔;
// shadow sides are carried by the hemisphere/ambient lights instead.
export const TOON_GRADIENT = ramp([0, 0, 0, 0.45, 0.72, 1.0]);

/** The lit-band defaults `rebakeRamp` restores to (upper half of the ramp). */
export const RAMP_DEFAULTS: [number, number, number] = [0.45, 0.72, 1.0];

// ---------------------------------------------------------------------------
// The CURVED-PRIM ramp (plan 27). The world is boxes: a flat face points ONE
// way, lands on ONE band of TOON_GRADIENT, and reads as a single flat color —
// cel banding never shows. But a genuinely curved, smooth-normalled surface
// (the `bone`/`lathe` character prims) sweeps N·L continuously and crosses all
// three lit bands → visible rings on a head/torso. This ramp keeps the same
// back-facing-black rule (lower half = 0, so lights don't bleed through
// geometry) but grades the lit half smoothly AND uses LinearFilter, so curves
// read as one soft gradient with no rings. Flat/faceted geometry stays on the
// chunky TOON_GRADIENT and keeps its crisp 3-band cel look.
export const TOON_SMOOTH = ramp(
  [0, 0, 0, 0, 0, 0, 0, 0, 0.44, 0.52, 0.61, 0.71, 0.81, 0.9, 0.96, 1.0],
  THREE.LinearFilter,
);

/**
 * Rewrite the shared ramp's lit bands IN PLACE (never resize — same-size texel
 * updates are a cheap texSubImage2D; every toon material shares this one
 * texture, so a rebake restyles the whole world live). The lower half is
 * pinned BLACK mechanically — the back-facing-light rule above. `tint` cools
 * the two darker lit bands toward a hue (classic cel trick: shadows shift
 * hue, not just value); the brightest band stays pure so lit color is exact.
 */
export function rebakeRamp(
  upper: [number, number, number] = RAMP_DEFAULTS,
  tint?: { color: number; amount: number },
): void {
  const data = TOON_GRADIENT.image.data as Uint8Array;
  const tc = new THREE.Color(tint?.color ?? 0xffffff);
  const stops = [0, 0, 0, ...upper];
  for (let i = 0; i < 6; i++) {
    const v = stops[i]!;
    // Tint strength fades to 0 on the brightest band (i=5) and is strongest
    // on the darkest lit band (i=3); the black half ignores it (v=0).
    const amt = i >= 3 && i < 5 && tint ? tint.amount * (i === 3 ? 1 : 0.5) : 0;
    data[i * 4] = Math.round(v * 255 * (1 - amt + amt * tc.r));
    data[i * 4 + 1] = Math.round(v * 255 * (1 - amt + amt * tc.g));
    data[i * 4 + 2] = Math.round(v * 255 * (1 - amt + amt * tc.b));
  }
  TOON_GRADIENT.needsUpdate = true;
}

// ---------------------------------------------------------------------------
// Rim/fresnel light (plan 23). ONE shared uniform object per knob — every
// toonified material's shader points at these same objects (the SWAY_TIME
// pattern), so the F6 dev panel mutates them and the whole world updates with
// zero per-material bookkeeping. These ARE the tunables (client-visual only;
// bake chosen values into the defaults here).
// ---------------------------------------------------------------------------
/** The rim-family defaults — ONE source of truth (the RAMP_DEFAULTS pattern),
 *  so the F6 "reset all" and the documented "bake chosen values into the
 *  defaults" workflow can't drift apart. Edit these to rebaseline the look. */
export const RIM_DEFAULTS = { strength: 0.32, power: 2.5, color: 0xffffff, nightDamp: 0.6 };

export const RIM = {
  strength: { value: RIM_DEFAULTS.strength }, // master rim amount (0 disables)
  power: { value: RIM_DEFAULTS.power }, // fresnel exponent — higher = thinner edge
  night: { value: 1.0 }, // written per-frame by game.ts (damped at night)
  color: { value: new THREE.Color(RIM_DEFAULTS.color) },
};

/** How much the rim damps at full night (game.ts: night = 1 - day factor →
 *  `RIM.night.value = 1 - night * damp`). 0 = rim glows all night; 1 = rim
 *  vanishes in the dark. Tunable from the F6 panel. */
export const TOON_NIGHT = { damp: RIM_DEFAULTS.nightDamp };

// ---- Rim enable (quality preset) -------------------------------------------
// The rim's pow/dot ALU runs on EVERY toon fragment, so Low compiles it OUT
// entirely rather than zeroing the uniform (which still pays the math). State
// lives here so this module stays DOM-free (the server world-build path); the
// CLIENT flips it (render.ts, from GraphicsSettings.rim). Every toonified
// material registers below so the flip can force a program re-pick — the
// program cache key tracks the flag, and materials de-register on dispose.
let rimEnabled = true;
const rimMaterials = new Set<THREE.Material>();
export function setRimEnabled(on: boolean): void {
  if (on === rimEnabled) return;
  rimEnabled = on;
  // Re-pick programs lazily: the next render sees a changed cache key per
  // material and compiles (or cache-hits) the matching rim/no-rim variant.
  for (const m of rimMaterials) m.needsUpdate = true;
}

/**
 * Inject the rim term into a MeshToonMaterial. Composes over any EXISTING
 * onBeforeCompile (the instanced-deco sway hook) by calling it first, and
 * extends the material's customProgramCacheKey so rim/sway program variants
 * never collide in the program cache. r185 anchors (verified): the fragment's
 * `#include <opaque_fragment>` sits right after `outgoingLight` is assembled
 * and before tonemapping/fog; `normal`, `vViewPosition`, `diffuseColor` (post
 * color_fragment — already × vertex/instance color) are all in scope there.
 *
 * `opts.rim` is a per-material trim on the shared strength (water/clouds).
 * NOTE: Material.clone() drops onBeforeCompile + customProgramCacheKey — a
 * clone of a toonified material silently loses the rim. No game code clones
 * toon materials today; keep it that way (re-toonify if that ever changes).
 */
export function toonify<M extends THREE.MeshToonMaterial>(m: M, opts: { rim?: number } = {}): M {
  const rimScale = { value: opts.rim ?? 1 };
  const prevOBC = m.onBeforeCompile;
  const prevKey = m.customProgramCacheKey.bind(m);
  m.onBeforeCompile = (shader, renderer) => {
    prevOBC.call(m, shader, renderer); // sway (or the empty default) runs first
    if (!rimEnabled) return; // Low: rim compiled out (cache key tracks the flag)
    shader.uniforms.uRimStrength = RIM.strength;
    shader.uniforms.uRimPower = RIM.power;
    shader.uniforms.uRimNight = RIM.night;
    shader.uniforms.uRimColor = RIM.color;
    shader.uniforms.uRimScale = rimScale;
    shader.fragmentShader =
      "uniform float uRimStrength;\nuniform float uRimPower;\nuniform float uRimNight;\n" +
      "uniform vec3 uRimColor;\nuniform float uRimScale;\n" +
      shader.fragmentShader.replace(
        "#include <opaque_fragment>",
        "float rimFres = pow( 1.0 - saturate( dot( normal, normalize( vViewPosition ) ) ), uRimPower );\n" +
          "outgoingLight += uRimColor * diffuseColor.rgb * ( rimFres * uRimStrength * uRimNight * uRimScale );\n" +
          "#include <opaque_fragment>",
      );
  };
  m.customProgramCacheKey = () => prevKey() + (rimEnabled ? "|rim1" : "|rim0");
  rimMaterials.add(m);
  m.addEventListener("dispose", () => rimMaterials.delete(m));
  return m;
}

/**
 * The choke point for the world's bespoke (vertex-colored / per-instance /
 * transparent) toon materials — terrain, water, roads, structures, clouds, the
 * studio terrain. Enforces the invariant "a toon material is born
 * gradient-mapped AND rimmed" in ONE place so a new site can't forget either
 * half (`toonMat(color)` above is the twin for solid-color prim bodies). Pass
 * the material's own params (color/vertexColors/transparent/…); the factory
 * folds in `gradientMap` and wraps in `toonify`. `opts.rim` trims the rim
 * relative to global strength (water/clouds over-glow at full). NOT for
 * materials that carry their own onBeforeCompile (the instanced-deco sway hook)
 * — those call `toonify` AFTER their hook is attached, to compose over it.
 */
export function makeToon(
  params: THREE.MeshToonMaterialParameters = {},
  opts: { rim?: number } = {},
): THREE.MeshToonMaterial {
  return toonify(new THREE.MeshToonMaterial({ ...params, gradientMap: TOON_GRADIENT }), opts);
}

/**
 * MeshToonMaterial has NO `flatShading` flag (unlike Lambert), so to keep the
 * faceted low-poly look we bake flat normals into the geometry: split shared
 * vertices (toNonIndexed) then recompute — each triangle then gets one normal.
 */
export function flatten(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  g.computeVertexNormals();
  return g;
}

/** Split shared vertices but KEEP the existing (smooth) normals — for rounded boxes. */
export function unindex(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  return geo.index ? geo.toNonIndexed() : geo;
}

// A per-color, per-ramp toon-material cache (one material instance reused
// everywhere for a given color). Each ramp gets its own cache + closure.
function makeCachedToon(gradientMap: THREE.Texture): (color: number) => THREE.MeshToonMaterial {
  const cache = new Map<number, THREE.MeshToonMaterial>();
  return (color: number) => {
    let m = cache.get(color);
    if (!m) {
      m = toonify(new THREE.MeshToonMaterial({ color, gradientMap }));
      cache.set(color, m);
    }
    return m;
  };
}

// Toon materials cached per color (one material instance reused everywhere).
export const toonMat = makeCachedToon(TOON_GRADIENT);

// The curved-prim twin of toonMat: same rim/night treatment, but sampling the
// smooth ramp (TOON_SMOOTH) so bone/lathe masses read as a soft gradient, not
// cel rings. Separate cache — the two materials differ only by gradientMap.
export const toonMatSmooth = makeCachedToon(TOON_SMOOTH);
